import {
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback
} from "react";

import {
  useGetDataObjectList,
  useGetObjectsStatuses
} from "@certa/queries/hooks/dataObjects.hooks";
import { Stack } from "@certa/catalyst/layouts/Stack";
import type { SortDirection, TableUnifiedColumn, Row } from "@certa/datagrid";
import { TableUnified, FILTER_TYPES, useTableFilters } from "@certa/datagrid";
import {
  getDataObjectTableCell,
  getDataObjectTableColumns,
  getDataObjectTableRows,
  getDateTimeRangeValues,
  DEFAULT_INCLUDED_COLUMNS,
  useCreateFilterParams
} from "@certa/common/utils/dataObject/table";
import { useObjectTypeTranslationsManager } from "@certa/common/store/dataObjects/useObjectTypeTranslationsManager";
import { analyticsService, AnalyticsEvents } from "@certa/analytics";

import type {
  DataObjectTableProps,
  DataObjectTableRef
} from "./DataObjectTable.types.ts";
import { useIntl } from "react-intl";
import { TextInput } from "@certa/catalyst/components/Inputs";
import { Search } from "@certa/icons/components/Search";
import { useDebouncedValue } from "@certa/common/hooks/useDebounce";
import {
  Typography,
  TypographyVariants
} from "@certa/catalyst/components/Typography";
import styles from "./DataObjectTable.module.css";
import {
  convertBadgeDefinitionsToBadges,
  useBadgeDefinitions
} from "@certa/queries/hooks/instanceSettings.hook";
import type { DateRangeValuesType } from "@certa/types";
import type { TableImperativeHandle } from "@certa/datagrid/src/componentsNew/Table/Table.types";

const PAGE_SIZE = 20;
const DEBOUNCE_TIME = 600;

// Type for the data object table row data - using the return type of getDataObjectTableRows
type DataObjectTableRowData = NonNullable<
  ReturnType<typeof getDataObjectTableRows>
>[number];

const DATA_TABLE_FILTERS = [
  {
    name: "search",
    type: FILTER_TYPES.TEXT
  },
  {
    name: "status",
    type: FILTER_TYPES.SELECT
  },
  {
    name: "created_at",
    type: FILTER_TYPES.DATE_RANGE
  },
  {
    name: "updated_at",
    type: FILTER_TYPES.DATE_RANGE
  }
] as const;

type TableRow = Row<DataObjectTableRowData>;

export const DataObjectTable = forwardRef<
  DataObjectTableRef,
  DataObjectTableProps
>((props, ref) => {
  const {
    // This is especially used for the object list page
    objectType: objectTypeTag = "",
    outgoingObjectId,
    relationName,
    rightActions,
    tabTitle,
    _getObjectTypeUISchema,
    tableProps,
    inputContainerProps,
    searchInputProps,
    onDataChange,
    uidList,
    shouldShowSearch = true,
    includedDefaultColumns = DEFAULT_INCLUDED_COLUMNS,
    shouldShowBadges = true,
    customColumns,
    allowedStatusTags
  } = props;

  const intl = useIntl();
  const tableRef = useRef<TableImperativeHandle>(null);

  const { data: objectsStatuses } = useGetObjectsStatuses();

  // Get status IDs for allowed status tags
  const allowedStatusIds = useMemo(() => {
    if (!objectsStatuses || !allowedStatusTags) return undefined;

    return objectsStatuses
      .filter(status => allowedStatusTags.includes(status.tag))
      .map(status => status.id.toString());
  }, [objectsStatuses, allowedStatusTags]);

  const {
    filters,
    onFilterChange,
    onSortChange,
    setCurrentPage,
    currentPage,
    offset,
    sort,
    onClearFilters
  } = useTableFilters({
    filters: {
      columns: DATA_TABLE_FILTERS
    },
    sort: {
      columns: ["created_at", "updated_at"] as const,
      initialState: {
        columnId: "created_at",
        direction: "desc"
      }
    }
  });

  const {
    search,
    status,
    created_at: createdAt,
    updated_at: updatedAt,
    ...dynamicFilters
  } = filters;
  const searchDebounced = useDebouncedValue(search, DEBOUNCE_TIME);

  const filterParams = useCreateFilterParams(dynamicFilters);

  const { data, isLoading, refetch } = useGetDataObjectList({
    objectTag: objectTypeTag,
    relation_name: relationName,
    related_object_uid: outgoingObjectId,
    search: searchDebounced,
    status__in: status?.length ? status.join(",") : allowedStatusIds?.join(","),
    created_at__date_range_before: createdAt?.end_date ?? undefined,
    created_at__date_range_after: createdAt?.start_date ?? undefined,
    updated_at__date_range_before: updatedAt?.end_date ?? undefined,
    updated_at__date_range_after: updatedAt?.start_date ?? undefined,
    ordering: sort?.orderBy,
    limit: PAGE_SIZE,
    offset: offset,
    uid__in: uidList && uidList.length > 0 ? uidList.join(",") : undefined,
    ...filterParams
  });

  const statusOptions = useMemo(() => {
    if (!objectsStatuses) return [];

    const allOptions = objectsStatuses.map(status => ({
      label: status.name,
      value: status.id.toString()
    }));

    // If allowedStatusTags is undefined, show all options
    if (!allowedStatusTags) return allOptions;

    // Filter options based on allowedStatusIds
    return allOptions.filter(option =>
      allowedStatusIds?.includes(option.value)
    );
  }, [objectsStatuses, allowedStatusTags, allowedStatusIds]);

  const { getTranslatedUISchema, getObjectTypeByTag } =
    useObjectTypeTranslationsManager();
  const objectTypeTranslatedUISchema = getTranslatedUISchema(
    relationName || objectTypeTag
  );

  const totalCount = data?.count ?? 0;
  const objectInstances = data?.results;
  const currentObjectType = data?.objectType;
  const filtersByField = currentObjectType?.tag
    ? getObjectTypeByTag(currentObjectType.tag)?.filtersByField
    : null;

  useEffect(() => {
    if (searchDebounced && currentObjectType?.tag) {
      analyticsService.track(
        AnalyticsEvents.dataObjectEvents.DATA_OBJECT_TABLE_SEARCH,
        {
          object_type: currentObjectType.tag,
          search_term: searchDebounced,
          relation_name: relationName
        }
      );
    }
  }, [searchDebounced, currentObjectType?.tag, relationName]);

  useEffect(() => {
    analyticsService.track(
      AnalyticsEvents.dataObjectEvents.DATA_OBJECT_TABLE_VIEW,
      {
        object_type: objectTypeTag,
        relation_name: relationName
      }
    );
  }, [objectTypeTag, relationName]);

  const handleFilterChangeAnalyticsTracking = (columnName: string) => {
    analyticsService.track(
      AnalyticsEvents.dataObjectEvents.DATA_OBJECT_TABLE_FILTER,
      {
        object_type: objectTypeTag,
        column_name: columnName,
        relation_name: relationName
      }
    );
  };

  const handleSortChangeAnalyticsTracking = (
    columnName: string,
    sortDirection?: SortDirection
  ) => {
    analyticsService.track(
      AnalyticsEvents.dataObjectEvents.DATA_OBJECT_TABLE_SORT,
      {
        object_type: objectTypeTag,
        column_name: columnName,
        direction: sortDirection,
        relation_name: relationName
      }
    );
  };

  const handleDynamicFilterChange = (
    key: string,
    value: string[] | string | DateRangeValuesType | undefined
  ) => {
    onFilterChange(key, value);
    handleFilterChangeAnalyticsTracking(key);
  };

  const handleDynamicSortChange = (
    columnName: string,
    sortDirection?: SortDirection
  ) => {
    onSortChange(columnName, sortDirection);
    handleSortChangeAnalyticsTracking(columnName, sortDirection);
  };

  const handleExternalFilterReset = useCallback(() => {
    onClearFilters();
    tableRef.current?.updateColumnFilters([]);
    tableRef.current?.updateColumnSorting([]);
  }, [onClearFilters]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    analyticsService.track(
      AnalyticsEvents.dataObjectEvents.DATA_OBJECT_TABLE_PAGINATION,
      {
        object_type: objectTypeTag,
        page: page,
        total_count: totalCount,
        relation_name: relationName
      }
    );
  };

  if (_getObjectTypeUISchema && currentObjectType?.uiSchema) {
    _getObjectTypeUISchema(
      currentObjectType.uiSchema,
      objectTypeTranslatedUISchema
    );
  }

  const isBadgesColumnVisible =
    shouldShowBadges &&
    !!currentObjectType?.uiSchema?.badges?.objects_table?.is_visible;

  const { data: badgeDefinitions } = useBadgeDefinitions({
    enabled: isBadgesColumnVisible
  });
  const badges = convertBadgeDefinitionsToBadges(badgeDefinitions ?? []);

  const allDefaultColumns: TableUnifiedColumn[] = [
    {
      header: intl.formatMessage({
        id: "fields.name",
        defaultMessage: "Name"
      }),
      id: "name",
      width: 200,
      cell: ({ row }: { row: TableRow }) =>
        getDataObjectTableCell("name", {
          ...row.original,
          objectType: currentObjectType
        })
    },
    {
      header: intl.formatMessage({
        id: "commonTextInstances.status",
        defaultMessage: "Status"
      }),
      id: "status",
      width: 150,
      canFilter: true,
      filterType: FILTER_TYPES.SELECT,
      onFilter: (value: string[]) => {
        handleDynamicFilterChange("status", value);
      },
      filterProps: {
        label: "Status",
        options: statusOptions
      },
      cell: ({ row }: { row: TableRow }) =>
        getDataObjectTableCell("status", row.original)
    },
    {
      header: intl.formatMessage({
        id: "reports.createdAt",
        defaultMessage: "Created At"
      }),
      id: "created_at",
      width: 150,
      canSort: true,
      onSort: (value?: SortDirection) => {
        handleDynamicSortChange("created_at", value);
      },
      cell: ({ row }: { row: TableRow }) =>
        getDataObjectTableCell("createdAt", row.original),
      canFilter: true,
      filterType: FILTER_TYPES.DATE_RANGE,
      filterProps: {
        disableFutureDates: true
      },
      onFilter: (value: DateRangeValuesType | undefined) => {
        const dateTimeRangeValues = value
          ? getDateTimeRangeValues(value, "datetime")
          : undefined;
        handleDynamicFilterChange("created_at", dateTimeRangeValues);
      }
    },
    {
      header: intl.formatMessage({
        id: "reports.updatedAt",
        defaultMessage: "Updated At"
      }),
      id: "updated_at",
      width: 150,
      canSort: true,
      onSort: (value?: SortDirection) => {
        handleDynamicSortChange("updated_at", value);
      },
      cell: ({ row }: { row: TableRow }) =>
        getDataObjectTableCell("updatedAt", row.original),
      canFilter: true,
      filterType: FILTER_TYPES.DATE_RANGE,
      filterProps: {
        disableFutureDates: true
      },
      onFilter: (value: DateRangeValuesType | undefined) => {
        const dateTimeRangeValues = value
          ? getDateTimeRangeValues(value, "datetime")
          : undefined;
        handleDynamicFilterChange("updated_at", dateTimeRangeValues);
      }
    }
  ];

  const { defaultColumns, dynamicColumns } = getDataObjectTableColumns({
    objectType: currentObjectType,
    objectTypeTranslatedUISchema,
    filtersByField,
    onSortChange: handleDynamicSortChange,
    onFilterChange: handleDynamicFilterChange,
    customColumns,
    allDefaultColumns,
    includedDefaultColumns
  });

  const badgesColumn: TableUnifiedColumn = {
    header: intl.formatMessage({
      id: "commonTextInstances.badges",
      defaultMessage: "Badges"
    }),
    id: "badges",
    width: 190,
    cell: ({ row }: { row: TableRow }) =>
      getDataObjectTableCell("badges", {
        ...row.original,
        allBadges: badges
      })
  };

  const columnsRef = useRef<TableUnifiedColumn[]>([]);

  // This is done to ensure there is no flickering between the default columns
  // and the dynamic columns while the data is loading
  if (!isLoading) {
    columnsRef.current = [...defaultColumns, ...dynamicColumns];

    // Insert badges column at the specified position with robust edge case handling
    if (isBadgesColumnVisible) {
      const totalColumns = columnsRef.current.length;
      const rawPosition =
        currentObjectType?.uiSchema?.badges?.objects_table?.column_position ??
        0;

      // column_position is relative to the position after default columns
      // So position 0 means right after the default columns (at index defaultColumns.length)
      const basePosition = defaultColumns.length + Math.max(0, rawPosition);

      // Clamp to valid range: [defaultColumns.length, totalColumns]
      // This ensures the badge column is always inserted at a valid position
      const clampedPosition = Math.min(basePosition, totalColumns);

      columnsRef.current.splice(clampedPosition, 0, badgesColumn);
    }
  }

  const objectDataRows = getDataObjectTableRows(objectInstances || []);
  const searchPlaceholder = intl.formatMessage({
    id: "homepage.searchbar.typeToSearch",
    defaultMessage: "Search..."
  });

  // reset filters when relation or object type changes
  useEffect(() => {
    handleExternalFilterReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relationName, objectTypeTag]);

  const currentObjectTag = currentObjectType?.tag;
  const currentObjectTypeDisplayNameSingular =
    currentObjectType?.uiSchema?.display_names?.singular;
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        objectDataRowsLength: objectDataRows.length,
        isLoading,
        currentObjectTag,
        currentObjectTypeDisplayNameSingular
      });
    }
  }, [
    objectDataRows.length,
    isLoading,
    currentObjectTag,
    currentObjectTypeDisplayNameSingular,
    onDataChange
  ]);

  // Expose refetch and resetPagination methods via ref
  useImperativeHandle(
    ref,
    () => ({
      refetch: () => {
        refetch?.();
      },
      resetPagination: () => {
        setCurrentPage(1);
      }
    }),
    [refetch, setCurrentPage]
  );

  return (
    <Stack direction="vertical" gap="16">
      {(tabTitle || shouldShowSearch || rightActions) && (
        <Stack
          direction="horizontal"
          gap="16"
          justify="space-between"
          {...inputContainerProps}
        >
          {tabTitle && (
            <Stack justify="center" height="2.25rem">
              <Typography
                className={styles.title}
                variant={TypographyVariants.BODY_BOLD}
              >
                {tabTitle}
              </Typography>
            </Stack>
          )}
          {(shouldShowSearch || rightActions) && (
            <Stack
              direction="horizontal"
              gap="16"
              dangerouslySetClassName={styles.wrapSearchInput}
            >
              {shouldShowSearch && (
                <TextInput
                  label="search"
                  value={filters.search ?? ""}
                  onChange={value => onFilterChange("search", value)}
                  allowClear
                  placeholder={searchPlaceholder}
                  leftIcon={<Search color="neutral-70" />}
                  width="280px"
                  {...searchInputProps}
                />
              )}
              {rightActions ? (
                <div className={styles.noWrapButton}>{rightActions}</div>
              ) : null}
            </Stack>
          )}
        </Stack>
      )}

      <Stack direction="vertical" gap="16" overflow="auto" height="100%">
        <TableUnified
          ref={tableRef}
          isLoading={isLoading}
          sorting={{ isManual: true }}
          filtering={{ isManual: true }}
          caption={`${objectTypeTag} List`}
          columns={columnsRef.current}
          data={objectDataRows}
          pagination={{
            isManual: true,
            state: { pageIndex: currentPage, pageSize: 20 },
            totalCount: totalCount,
            onChange: handlePageChange
          }}
          columnPinning={{ enabled: false }}
          columnResizing={{ enabled: true }}
          columnOrdering={{ enabled: false }}
          {...tableProps}
        />
      </Stack>
    </Stack>
  );
});
